import { action, makeObservable } from 'mobx';
import { fn } from 'firebase-modules';
import type { RootStore } from '.';

/**
 * Org owner/admin session actions (CRE-253). CQ-admin paths use uiCQAdminStore.
 */
export class UiOrganizationSessionStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      restoreSession: action.bound,
      requestDeleteSession: action.bound,
      requestOwnershipTransfer: action.bound,
      transferOrphanOwnership: action.bound,
    });
    this.rootStore = rootStore;
  }

  async restoreSession(sessionId: string): Promise<void> {
    try {
      const callable = fn.httpsCallable('adminRestoreSession');
      await callable({ sessionId });
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Project restored to the owner workspace.',
      });
    } catch (e) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: e instanceof Error ? e.message : 'Failed to restore project',
      });
      throw e;
    }
  }

  async requestDeleteSession(sessionId: string, reason?: string): Promise<void> {
    try {
      const callable = fn.httpsCallable('requestSessionDeletion');
      await callable({
        sessionId,
        reason: reason?.trim().slice(0, 500) ?? '',
        requesterRole: 'org_admin',
      });
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Deletion request sent to the project owner.',
      });
    } catch (e) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: e instanceof Error ? e.message : 'Failed to send deletion request',
      });
      throw e;
    }
  }

  async requestOwnershipTransfer(
    sessionId: string,
    newOwnerId: string,
    reason?: string,
  ): Promise<void> {
    try {
      const callable = fn.httpsCallable('requestSessionOwnershipTransfer');
      await callable({
        sessionId,
        newOwnerId,
        reason: reason?.trim().slice(0, 500) ?? '',
      });
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Ownership transfer request sent to the project owner.',
      });
    } catch (e) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: e instanceof Error ? e.message : 'Failed to send transfer request',
      });
      throw e;
    }
  }

  async transferOrphanOwnership(sessionId: string, newOwnerId: string): Promise<void> {
    try {
      const callable = fn.httpsCallable('transferSessionOwnership');
      await callable({ sessionId, newOwnerId, isOrgAdminOrphanTransfer: true });
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Ownership transferred.',
      });
    } catch (e) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: e instanceof Error ? e.message : 'Failed to transfer ownership',
      });
      throw e;
    }
  }
}
